import { getHighLevelStatus, HighLevelStatus } from '@/src/listings/utils/highLevelStatus';
import { useTicketInformation } from '@/src/listings/hooks/useTicketInformation/useTicketInformation';
import type { WebappResponse } from '@eventbrite/event-listing-service-client';
import React, { createContext, useContext, ReactNode } from 'react';

export type DisplayStatusContextType = {
  salesStatusData?: { salesStatus?: string; messageCode?: string };
  displayStatus: HighLevelStatus;
  isDisplayStatusStable: boolean;
};

const DisplayStatusContext = createContext<DisplayStatusContextType>({
  displayStatus: HighLevelStatus.UNKNOWN,
  isDisplayStatusStable: false,
});

type DisplayStatusProviderProps = {
  children: ReactNode;
  salesStatusData?: WebappResponse['salesStatus'];
  eventStatus?: string;
};

export const DisplayStatusProvider: React.FC<DisplayStatusProviderProps> = ({
  children,
  salesStatusData: ssrSalesStatusData,
  eventStatus,
}) => {
  const [ticketInfo] = useTicketInformation();
  const salesStatusChanged = ticketInfo && ticketInfo.salesStatus !== ssrSalesStatusData?.salesStatus;
  const salesStatusData = salesStatusChanged ? { salesStatus: ticketInfo.salesStatus } : ssrSalesStatusData;

  const isDisplayStatusStable = !ticketInfo || !salesStatusChanged;

  return (
    <DisplayStatusContext.Provider
      value={{
        salesStatusData,
        displayStatus: getHighLevelStatus(salesStatusData, eventStatus),
        isDisplayStatusStable,
      }}
    >
      {children}
    </DisplayStatusContext.Provider>
  );
};

export const useDisplayStatus = (): DisplayStatusContextType => {
  const context = useContext(DisplayStatusContext);
  if (context === undefined) {
    throw new Error('useDisplayStatus must be used within a DisplayStatusProvider');
  }
  return context;
};
